import { BigIntWithDecimals, ChecksumAddress, ProtocolKey, USD_DECIMALS } from '@silo/types';
import { CheckmarkIcon, ExternalLinkIcon, Link } from '@silo/ui';
import { asDecimal, asFullUSD, isUndefined } from '@silo/utils';
import { getExplorerLink, getExplorerName, useSiloWeb3 } from '@silo/web3';
import { ContractTransaction } from 'ethers';
import { FC } from 'react';
import { AssetLogo } from '../common/icons/AssetLogo';
import { isNotNullable } from 'effect/Predicate';

interface RewardsInUSDProps {
  totalBalanceUSD?: BigIntWithDecimals;
  className?: string;
}

export const RewardsInUSD: FC<RewardsInUSDProps> = ({ totalBalanceUSD, className }) => {
  if (isUndefined(totalBalanceUSD) || totalBalanceUSD.value === 0n) {
    return null;
  }

  return <span className={`font-normal text-shale-200 ${className}`}>{asFullUSD(totalBalanceUSD)}</span>;
};

interface ClaimSuccessProps {
  data: ContractTransaction;
}

export function ClaimSuccess({ data }: ClaimSuccessProps): JSX.Element {
  return (
    <div className="flex flex-col gap-6 md:gap-5">
      <h2 className="text-xl font-medium flex items-center gap-2.5">
        <CheckmarkIcon className="w-6 h-6" />
        <span>Rewards claimed successfully</span>
      </h2>

      <TransactionLink hash={data.hash} />
    </div>
  );
}

interface TransactionLinkProps {
  hash: string;
}

const TransactionLink: FC<TransactionLinkProps> = ({ hash }) => {
  const { chainId } = useSiloWeb3();

  const explorerLink = chainId ? getExplorerLink(chainId, hash, 'transaction') : '#';

  return (
    <Link href={explorerLink} external className="flex items-center gap-1">
      <span>View transaction on {getExplorerName(chainId)}</span>
      <ExternalLinkIcon className="w-5 h-5" />
    </Link>
  );
};

interface BalanceDisplayProps {
  protocolKey: ProtocolKey;
  rewardTokenAddress: ChecksumAddress;
  rewardTokenSymbol: string;
  rewardTokenDecimals: number;
  balance: bigint;
  balanceUsd?: bigint;
}

export function BalanceDisplay({
  protocolKey,
  rewardTokenAddress,
  rewardTokenSymbol,
  rewardTokenDecimals,
  balance,
  balanceUsd,
}: BalanceDisplayProps): JSX.Element {
  return (
    <div className="rounded-lg bg-shale-600 px-5 py-2.5 flex items-center gap-5 justify-between">
      <div className="flex items-center gap-5">
        <div className="flex items-center gap-2.5">
          <AssetLogo protocolInstance={protocolKey} address={rewardTokenAddress} size="md" />
          <span className="font-medium">{rewardTokenSymbol}</span>
        </div>
        <div className="self-stretch w-px bg-shale-500"></div>
        <div className="font-mono font-medium text-shale-200">
          {asDecimal({
            value: balance,
            decimals: rewardTokenDecimals,
          })}
        </div>
      </div>

      <RewardsInUSD
        totalBalanceUSD={isNotNullable(balanceUsd) ? { value: balanceUsd, decimals: USD_DECIMALS } : undefined}
        className="font-mono"
      />
    </div>
  );
}
